import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { getUserRole, ROLES } from '../utils/roles';

// ═══════════════════════════════════════════════════════════
//  REKOD VERSI SKRIP AUTOMATION  (admin sahaja)
//  - Baca config/script_versions (live, onSnapshot).
//  - Papar versi semasa setiap skrip + sejarah bump.
//  - Sumber kebenaran versi SEBENAR = pemalar dalam setiap skrip
//    (cth SESAAT_FLOW_V3_VERSION). Doc Firestore ini = cermin untuk
//    paparan + log sejarah, ditulis setiap kali Claude bump versi
//    selepas user sahkan.
//  - SEED bawah = jaring keselamatan: dipapar HANYA kalau doc belum
//    wujud, supaya halaman tak pernah kosong.
// ═══════════════════════════════════════════════════════════

const SEED = {
  // Baseline 1.0.0 (reset 2026-09-04, arahan user) — semua skrip mula semula
  // dari 1.0.0 sebagai titik permulaan sistem rekod ini. Nombor lama yang
  // beku (tiktok 3.1.2, flow 1.1.2/1.2.4/1.3.0, common 1.8.0) dibuang.
  scripts: [
    { id: 'flow-ai8saat', label: 'Flow — AI8SAAT', file: 'flow-automation-v3.js', version: '1.0.0' },
    { id: 'flow-ai16saat', label: 'Flow — AI16SAAT / Home Niche', file: 'flow-automation-ai16saat-v3.js', version: '1.0.0' },
    { id: 'flow-goyang', label: 'Flow — GOYANG', file: 'flow-automation-goyang-v3.js', version: '1.0.0' },
    { id: 'flow-flownity', label: 'Flow — FLOWNITY', file: 'flow-automation-flownity.js', version: '1.0.0' },
    { id: 'flow-dinamik', label: 'Flow — Dinamik', file: 'flow-automation-dinamik.js', version: '1.0.0' },
    { id: 'flow-common', label: 'Flow — Common (dikongsi semua Flow)', file: 'flow-common.js', version: '1.0.0' },
    { id: 'tiktok', label: 'TikTok Upload', file: 'tiktok-automation.js', version: '1.0.0' },
    { id: 'facebook', label: 'Facebook Reels (Shopee affiliate)', file: 'facebook-reels.js', version: '1.1.0' },
  ],
  history: [
    {
      at: '2026-09-04',
      scriptId: 'facebook',
      label: 'Facebook Reels',
      from: '1.0.0',
      to: '1.1.0',
      note: 'Baiki butang buka komposer upload reels (dulu tersilap buka tab feed Reels) + tambah pengesahan di setiap langkah (buka komposer, caption, link Shopee, Post) supaya kegagalan tidak senyap.',
    },
  ],
};

function fmtDate(v) {
  if (!v) return '';
  let d;
  if (typeof v === 'object' && typeof v.seconds === 'number') d = new Date(v.seconds * 1000);
  else d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ScriptVersionsView() {
  const role = getUserRole(auth.currentUser?.email);
  const isAdmin = role === ROLES.ADMIN || role === ROLES.MODERATOR;
  const [data, setData] = useState(null); // null = memuatkan
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    const unsub = onSnapshot(
      doc(db, 'config', 'script_versions'),
      (snap) => { setErr(false); setData(snap.exists() ? snap.data() : SEED); },
      () => { setErr(true); setData(SEED); }
    );
    return () => unsub();
  }, [isAdmin]);

  if (!isAdmin) {
    return <div style={{ padding: 40, color: '#ef4444' }}>Akses ditolak — admin sahaja.</div>;
  }

  const scripts = (data && data.scripts && data.scripts.length) ? data.scripts : SEED.scripts;
  const history = (data && data.history) ? data.history : [];

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <div className="svr-wrap">
        <div className="svr-head">
          <h1 className="svr-title">Rekod Versi Skrip Automation</h1>
          <p className="svr-sub">Setiap kali satu skrip automation dibaiki, nombor versinya dinaikkan dan dicatat di sini — supaya anda boleh jejak skrip mana berubah, bila, dan kenapa. Nombor ini berasingan daripada versi extension.</p>
        </div>

        {data === null && <div className="svr-empty">Memuatkan…</div>}

        {data !== null && (
          <>
            <div className="svr-card">
              <div className="svr-card-h">Versi semasa</div>
              <table className="svr-table">
                <thead>
                  <tr><th>Skrip</th><th className="svr-th-ver">Versi</th></tr>
                </thead>
                <tbody>
                  {scripts.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div className="svr-name">{s.label}</div>
                        <div className="svr-file">{s.file}</div>
                      </td>
                      <td className="svr-td-ver"><span className="svr-ver">v{s.version}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="svr-card">
              <div className="svr-card-h">Sejarah perubahan</div>
              {history.length === 0 ? (
                <div className="svr-empty">Belum ada rekod perubahan lagi. Rekod akan muncul di sini secara automatik bila skrip pertama dinaikkan versi.</div>
              ) : (
                <ul className="svr-log">
                  {history.map((h, i) => (
                    <li key={i} className="svr-logitem">
                      <span className="svr-logdate">{fmtDate(h.at)}</span>
                      <span className="svr-logname">{h.label || h.scriptId}</span>
                      {(h.from || h.to) && <span className="svr-logver">{h.from || '—'} → {h.to || '—'}</span>}
                      {h.note && <span className="svr-lognote">{h.note}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {err && <div className="svr-warn">Tidak dapat baca pangkalan data sekarang — memaparkan senarai asas. Sila muat semula halaman.</div>}
          </>
        )}
      </div>

      <style>{`
        .svr-wrap{padding:24px 32px 80px;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;color:#1f2430;max-width:820px}
        .svr-head{margin-bottom:20px}
        .svr-title{font-size:20px;font-weight:800;margin:0 0 6px;letter-spacing:-.02em}
        .svr-sub{font-size:13px;color:#6b7280;margin:0;line-height:1.6;max-width:660px}
        .svr-card{border:1px solid #e5e7eb;border-radius:14px;background:#fff;padding:16px 18px;margin-bottom:18px}
        .svr-card-h{font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;margin-bottom:12px}
        .svr-table{width:100%;border-collapse:collapse}
        .svr-table th{text-align:left;font-size:10.5px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.04em;padding:0 0 8px;border-bottom:1px solid #f0f0f2}
        .svr-th-ver{text-align:right}
        .svr-table td{padding:11px 0;border-bottom:1px solid #f4f4f6;vertical-align:middle}
        .svr-table tr:last-child td{border-bottom:none}
        .svr-td-ver{text-align:right;white-space:nowrap}
        .svr-name{font-size:13.5px;font-weight:600;color:#1f2430}
        .svr-file{font-size:11px;color:#9ca3af;font-family:ui-monospace,'SF Mono',Menlo,monospace;margin-top:2px}
        .svr-ver{display:inline-block;font-size:12.5px;font-weight:700;color:#0f6e56;background:#e1f5ee;border:1px solid #9fe1cb;border-radius:999px;padding:3px 11px;font-family:ui-monospace,'SF Mono',Menlo,monospace}
        .svr-empty{font-size:13px;color:#9a93ad;background:#fafafa;border:1px dashed #e5e7eb;border-radius:12px;padding:22px;text-align:center;line-height:1.6}
        .svr-log{list-style:none;margin:0;padding:0;display:flex;flex-direction:column}
        .svr-logitem{display:flex;flex-wrap:wrap;align-items:baseline;gap:10px;padding:11px 0;border-bottom:1px solid #f4f4f6;font-size:12.5px}
        .svr-logitem:last-child{border-bottom:none}
        .svr-logdate{color:#9ca3af;font-size:11.5px;min-width:96px}
        .svr-logname{font-weight:700;color:#1f2430}
        .svr-logver{font-family:ui-monospace,'SF Mono',Menlo,monospace;font-size:11.5px;color:#0f6e56;background:#f0faf6;border-radius:6px;padding:1px 7px}
        .svr-lognote{color:#4b5563;flex:1;min-width:180px}
        .svr-warn{font-size:12px;color:#92400e;background:#fef3c7;border:1px solid #fcd34d;border-radius:10px;padding:10px 14px}
      `}</style>
    </div>
  );
}
